// src/lib/audit.ts
// Lightweight client-side audit logger for admin actions.
// Logs to console in dev and can be extended to POST to a backend endpoint.
// Never includes sensitive data (tokens, passwords, full payloads).

type AuditAction =
  | 'publish_attempt'
  | 'publish_success'
  | 'publish_error'
  | 'config_update'
  | 'permission_denied'
  | 'company_update'
  | 'module_toggle'
  | 'login'
  | 'logout';

interface AuditEvent {
  action: AuditAction;
  org_id: string | null;
  user_id: string | null;
  timestamp: string;
  meta?: Record<string, string | number | boolean>;
}

const IS_DEV = import.meta.env.DEV;

/** Emit an audit event. In dev, logs to console. Safe for production (no sensitive data). */
export function audit(action: AuditAction, meta?: Record<string, string | number | boolean>) {
  const event: AuditEvent = {
    action,
    org_id: localStorage.getItem('org_id'),
    user_id: localStorage.getItem('user_id'),
    timestamp: new Date().toISOString(),
    meta,
  };

  if (IS_DEV) {
    console.info(`📋 [audit] ${action}`, meta ?? '');
  }

  // Store last 100 events in sessionStorage for support debugging
  try {
    const key = '_audit_log';
    const existing: AuditEvent[] = JSON.parse(sessionStorage.getItem(key) || '[]');
    existing.push(event);
    // Keep only last 100
    if (existing.length > 100) existing.splice(0, existing.length - 100);
    sessionStorage.setItem(key, JSON.stringify(existing));
  } catch {
    // sessionStorage full or unavailable — ignore
  }
}

/** Retrieve stored audit events (for debugging/support). */
export function getAuditLog(): AuditEvent[] {
  try {
    return JSON.parse(sessionStorage.getItem('_audit_log') || '[]');
  } catch {
    return [];
  }
}
